import { RootState } from 'types/state';
import { FormErrorsUtil } from 'helpers/formErrors';
import { getFormStatus } from 'selectors/validation';
import { formSectionsSelector } from 'selectors/navigation';
import { NameModel, SectionSchema } from 'forms/shared/types';
import { sortSafeDateTimes, getValidatedDates } from 'helpers/date';
import { DateTime } from 'luxon';
import { DataAttributeType } from 'helpers/attributes/types';
import { hideExistingConditions as hideExistingConditionsFn } from '../../../validators/psychological';
import { SectionConfig, SubsectionConfig } from '../section/reducer';
import { FormStateSubsectionsKeys, FormError, FormState } from './types';

export const getForm = (state: RootState) => state.form;

export const getSubsection = (state: RootState, subsectionKey: string) => state.form[subsectionKey as FormStateSubsectionsKeys] ?? {};

// TODO: To optimize, we could use a memo selector from reselect.
/**
 * This function will return a true or false when all state.form sections are
 * completed. As a note, this skips "PACKAGE_SUBMIT" as part of that check. This
 * will also skip non-objects as well.
 */
export const areAllSectionsCompleted = (state: RootState): boolean => {
  const hideExistingConditions = hideExistingConditionsFn(state.application);
  const excludeSubsections = [
    'IDENTIFICATION_INTRO',
    'IDENTIFICATION_REVIEW',
    'CITIZENSHIP_INTRO',
    'CITIZENSHIP_REVIEW',
    'HISTORY_INTRO',
    'HISTORY_REVIEW',
    'MILITARY_INTRO',
    'MILITARY_REVIEW',
    'RELATIONSHIPS_INTRO',
    'RELATIONSHIPS_REVIEW',
    'SUBSTANCE_USE_INTRO',
    'SUBSTANCE_USE_REVIEW',
    'PACKAGE_SUBMIT',
    'SUBMISSION_RELEASES',
    hideExistingConditions && 'PSYCHOLOGICAL_CONDITIONS',
  ];

  return (
    formSectionsSelector(state)
      .map((section: SectionSchema) => section.key)
      .filter((key: keyof FormState) => typeof state.form[key] === 'object')
      .filter((key: keyof FormState) => !excludeSubsections.includes(key))
      .filter(
        (key: keyof FormState) => state.form[key as FormStateSubsectionsKeys]?.complete !== true
      ).length === 0
  );
};

export const hasAttributeErrors = (
  state: RootState,
  subsectionKey: FormStateSubsectionsKeys,
  attribute: string,
  showRequiredErrors: boolean,
  displayErrorsReviewOnly: string[] = []
): boolean => {
  const errors = state.form[subsectionKey]?.formErrors;
  const attributeErrors = errors?.filter((error) => error.attribute === attribute);

  return FormErrorsUtil(attributeErrors).handleShowRequiredErrors(showRequiredErrors, displayErrorsReviewOnly).length > 0;
};

/**
 * getApplicantSuggestedNames joins applicantName with OtherNames
 */
export const getApplicantSuggestedNames = (state: RootState): DeepSafePartial<NameModel[]> => {
  const names = state.form.IDENTIFICATION_OTHER_NAMES?.data?.List?.items
    ?.filter((i) => i?.Item?.Name)
    .map((i) => i?.Item?.Name) ?? [];

  const Name = state.form?.IDENTIFICATION_NAME?.data?.Name;

  return [...names, ...[Name]].filter((item) => item !== undefined);
};

interface SectionError extends SubsectionConfig {
  errors: FormError[]
  listHeader?: string
  listKey?: string
}
type SectionErrors = SectionError[]

export const getSectionErrors = (state: RootState, section: string): SectionErrors => {
  const result: SectionConfig = getFormStatus(state).find(
    (item: SectionConfig) => item.name === section
  );

  if (!result) { return []; }

  const errors: SectionErrors = [];
  const subsectionsForEach = (sub: SubsectionConfig) => {
    if (sub === undefined) { return; }
    if (sub.key === undefined) { return; }
    if (sub.subsections) { sub.subsections.forEach(subsectionsForEach); }
    if ((state.form[sub.key as FormStateSubsectionsKeys]?.formErrors?.length ?? 0) > 0) {
      errors.push({
        ...sub,
        errors: state.form[sub.key as FormStateSubsectionsKeys]?.formErrors ?? [],
      });
    }
  };

  result.subsections.forEach(subsectionsForEach);

  return errors;
};

export const getSubsectionErrors = (
  state: RootState,
  subsectionKey: FormStateSubsectionsKeys
): FormError[] => state.form[subsectionKey]?.formErrors ?? [];

export const getSubsectionErrorsByAttribute = (
  state: RootState,
  subsectionKey: FormStateSubsectionsKeys,
  attribute: DataAttributeType,
  exact?: boolean
): FormError[] => FormErrorsUtil(getSubsectionErrors(state, subsectionKey))
  .filterByAttribute(attribute, exact)
  .get();

/**
 * returns applicantBirthdate as DateModel
 */
export const getApplicantBirthdate = (state: RootState) => state.application.Identification.ApplicantBirthDate?.Date;
export const getSpouseBirthdate = (state: RootState) => state.application.Relationships?.Marital?.CivilUnion?.Birthdate;
export const getCohabitantBirthdates = (state: RootState) => state.application.Relationships?.Cohabitants?.CohabitantList?.items?.map(
  (item) => item?.Item?.Birthdate
) ?? [];

/**
 * returns oldest date between applicantBirthdate, Spouse Birthdate, and Cohabitant Birthdates
 */
export const getRelationshipsOldestBirthdate = (state: RootState): DateTime | undefined => {
  const applicantBirthdate = getApplicantBirthdate(state);
  const spouseBirthdate = getSpouseBirthdate(state);
  const cohabitantBirthdates = getCohabitantBirthdates(state);

  const sortedDates = sortSafeDateTimes(
    getValidatedDates([...cohabitantBirthdates, applicantBirthdate, spouseBirthdate])
  );

  return sortedDates.length ? sortedDates[0].datetime : undefined;
};

/**
 * returns youngest date between applicantBirthdate, Spouse Birthdate, and Cohabitant Birthdates
 */
export const getRelationshipsYoungestBirthdate = (state: RootState): DateTime | undefined => {
  const applicantBirthdate = getApplicantBirthdate(state);
  const spouseBirthdate = getSpouseBirthdate(state);
  const cohabitantBirthdates = getCohabitantBirthdates(state);

  const sortedDates = sortSafeDateTimes(
    getValidatedDates([...cohabitantBirthdates, applicantBirthdate, spouseBirthdate])
  );

  return sortedDates.length ? sortedDates[sortedDates.length - 1].datetime : undefined;
};
